/**
 * Config Approvals — dual-control (maker-checker) config change requests,
 * with the checker's decide action wired.
 *
 * Reads GET /api/agentic/config-approvals (backend/app/routes/agentic_config.py).
 * Pending requests carry a Review button → DecideApprovalDialog, which POSTs
 * /api/agentic/config-approvals/{id}/decide (agentic:config:approve; the
 * proposer can never decide their own request — service + DB CHECK). Approved
 * Safe-Mode exit authorizations are EXECUTED from Policies & Safety, not here.
 * On 403/404 → NotEnabled.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, GitPullRequest } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  KpiCard,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import {
  listConfigApprovals,
  SAFE_MODE_EXIT_CHANGE_TYPE,
  type ConfigApproval,
} from '../api/agentic';
import { isNotEntitled } from '../api/client';
import DecideApprovalDialog from '../components/DecideApprovalDialog';
import NotEnabled from '../components/NotEnabled';

const STATUS_BADGE: Record<string, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  approved: 'success',
  applied: 'success',
  rejected: 'danger',
  expired: 'neutral',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function decidedCaption(a: ConfigApproval): string {
  if (a.status === 'expired') return ' · expired undecided';
  if (a.checker_user_id) return ` · decided ${formatWhen(a.decided_at)}`;
  return ' · awaiting a distinct second approver';
}

export default function ConfigApprovalsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'config-approvals'],
    queryFn: () => listConfigApprovals(),
  });
  const [reviewing, setReviewing] = useState<ConfigApproval | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="approvals-loading">
        <Spinner size="lg" label="Loading config approvals" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load config approvals"
          description="Something went wrong while loading approvals. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const pending = data.filter(a => a.status === 'pending').length;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Config Approvals"
          description="Dual-control (maker-checker) change requests for safety-loosening config — threshold lowering, autonomy raises, Safe Mode exit, allow-list edits. A pending request is decided here by a second person holding approve rights; the proposer can never decide their own request."
        />

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard label="Pending" value={pending} />
            <KpiCard label="Total" value={data.length} />
            <KpiCard label="Dual-control" value={data.filter(a => a.requires_dual_control).length} />
          </div>
        </Section>

        <Section>
          {data.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                compact
                icon={<Icon as={GitPullRequest} size="lg" />}
                title="No config change requests"
                description="When someone proposes a safety-loosening config change, the maker-checker request appears here."
              />
            </Card>
          ) : (
            <Stack gap="sm">
              {data.map((a: ConfigApproval) => (
                <Card key={a.id} padding="md">
                  <Stack gap="sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge variant={STATUS_BADGE[a.status] ?? 'neutral'} size="sm">
                        {a.status}
                      </Badge>
                      <Heading level={2} className="min-w-0 flex-1 text-base">
                        {humanize(a.change_type)}
                        {a.agent_id ? ` · ${humanize(a.agent_id)}` : ''}
                      </Heading>
                      {a.requires_dual_control && (
                        <Badge variant="info" size="sm">
                          dual-control
                        </Badge>
                      )}
                      {a.status === 'pending' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setReviewing(a)}
                          aria-label={`Review ${humanize(a.change_type)} request`}
                        >
                          Review
                        </Button>
                      )}
                    </div>
                    <Text variant="caption" tone="muted">
                      Proposed {formatWhen(a.proposed_at)}
                      {decidedCaption(a)}
                    </Text>
                    {a.status === 'approved' && a.change_type === SAFE_MODE_EXIT_CHANGE_TYPE && (
                      <Text variant="caption" tone="muted">
                        Approved exit authorization (single use, valid 24h) — execute it from
                        Policies &amp; Safety.
                      </Text>
                    )}
                    <pre className="overflow-x-auto rounded-md bg-elevated p-3 text-xs text-text">
                      {JSON.stringify(a.proposed_change, null, 2)}
                    </pre>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Section>
      </Stack>

      <DecideApprovalDialog
        approval={reviewing}
        open={reviewing !== null}
        onOpenChange={open => {
          if (!open) setReviewing(null);
        }}
      />
    </div>
  );
}
